import React from "react";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { makeStyles } from "tss-react/mui";
import * as Icons from "react-feather";
import { AgentColumnType } from "type/agent-column-type";
import { Sort } from "component/shared/table";
import { RoleTag, RoutingTag } from "./member-meta";
import * as env from "shared/env";
import * as api from "api/api";

export interface TeamFilters {
	roles: RoleTag[];
	routing: RoutingTag[];
	/** Office ids. Seeded from ?officeId= so the Office detail view can deep-link here (MAX-4065). */
	offices: number[];
	/** Members who own leads, admins included. Seeded from ?billable=1 by the Account page's Billing tab. */
	billable: boolean;
}

export interface OfficeOption {
	id: number;
	label: string;
}

export interface SortOption {
	id: string;
	label: string;
	desc: boolean;
}

interface Props {
	term: string;
	onTermChange: (term: string) => void;
	sortOptions: SortOption[];
	sort: Sort;
	onSortChange: (sort: Sort) => void;
	filters: TeamFilters;
	onFiltersChange: (filters: TeamFilters) => void;
	officeOptions: OfficeOption[];
	columnOptions: AgentColumnType[];
	visibleColumns: AgentColumnType[];
	onColumnsChange: (columns: AgentColumnType[]) => void;
	showColumnControls: boolean;
	onAddMember: () => void;
}

const ROLE_OPTIONS: RoleTag[] = ["Admin", "Agent"];
const ROUTING_OPTIONS: RoutingTag[] = ["Round Robin", "Geographic"];

// Labels shown in the Filter > Roles section. The "Agent" role is presented as "Lead Owner"
// for consistency with the add/edit member flow and the Role column tag.
const ROLE_FILTER_LABELS: Record<RoleTag, string> = {
	Admin: "Admin",
	Agent: "Lead Owner (Billable)",
};

// Labels shown in the Columns dropdown (per Figma — distinct from the table header labels).
const COLUMN_TOGGLE_LABELS: Record<string, string> = {
	avatar: "User Avatar",
	label: "User",
	role: "Role",
	billableUser: "Billable",
	leads: "Leads",
	routing: "Routing",
	office: "Office",
	activeListings: "Active Listings",
};

const useStyles = makeStyles()((theme) => ({
	toolbar: {
		// The toolbar is a Grid container with spacing={2}, whose items add 8px of bottom
		// gutter below the controls. Adding 8px here yields the 16px visual gap to the table.
		marginBottom: theme.spacing(1),
	},
	// All toolbar controls are 48px tall per Figma (search, Filter, Columns, Export, Add Member).
	dropdownButton: {
		height: 48,
		textTransform: "none",
		fontWeight: 600,
		fontSize: 16,
		lineHeight: "20px",
		color: theme.palette.primary.main, // teal text per Figma
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.custom.borderCard}`,
		borderRadius: 4,
		padding: "12px 16px",
		"&:hover": {
			backgroundColor: theme.palette.background.paper,
			borderColor: theme.palette.custom.borderCard,
		},
	},
	actionButton: {
		height: 48,
		textTransform: "none",
		fontWeight: 600,
		fontSize: 16,
		borderRadius: 8,
		padding: "8px 16px",
	},
	searchField: {
		minWidth: 340,
		[theme.breakpoints.down("xs")]: {
			minWidth: "100%",
		},
		"& .MuiOutlinedInput-root": {
			height: 48,
			borderRadius: 4,
		},
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.custom.borderCard,
		},
	},
	// Filter/Columns dropdown — sized to the Figma "Dropdown_select" (222px wide, 48px rows).
	menuPaper: {
		width: 222,
		padding: 0,
	},
	sectionHeader: {
		fontWeight: 700,
		fontSize: 16,
		lineHeight: "24px",
		color: theme.palette.custom.textPrimary,
		padding: "12px 16px", // 48px-tall section header row
	},
	option: {
		padding: "12px 16px", // 48px-tall option row per Figma
		gap: 10, // control → label gap
	},
	// 24×24 controls, brand-orange. Strip MUI's default ripple padding so the row height is 48px.
	checkbox: {
		padding: 0,
		color: theme.palette.custom.primary, // brandOrange outline when unchecked (per Figma)
		"& svg": {
			fontSize: 24,
		},
		"&.Mui-checked": {
			color: theme.palette.custom.primary, // filled brandOrange when checked
		},
	},
	radio: {
		padding: 0,
		color: theme.palette.custom.tagNeutralSurface,
		"& svg": {
			fontSize: 24,
		},
		"&.Mui-checked": {
			color: theme.palette.custom.primary,
		},
	},
	optionLabel: {
		"& .MuiTypography-root": {
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
		},
	},
}));

export const TeamToolbar: React.FC<Props> = ({
	term,
	onTermChange,
	sortOptions,
	sort,
	onSortChange,
	filters,
	onFiltersChange,
	officeOptions,
	columnOptions,
	visibleColumns,
	onColumnsChange,
	showColumnControls,
	onAddMember,
}) => {
	const { classes } = useStyles();
	const [exporting, setExporting] = React.useState(false);
	const [exportFailed, setExportFailed] = React.useState(false);

	const handleExport = async () => {
		setExporting(true);
		try {
			await api.exportAgents();
		} catch{
			setExportFailed(true);
		} finally {
			setExporting(false);
		}
	};

	return (
		<Mui.Grid
			container
			alignItems="center"
			justifyContent="space-between"
			spacing={2}
			wrap="wrap"
			className={classes.toolbar}
		>
			<Mui.Grid item container xs={12} md={8} spacing={1} alignItems="center">
				<Mui.Grid item xs={12} sm={5}>
					<Mui.TextField
						fullWidth
						className={classes.searchField}
						variant="outlined"
						placeholder="Search users"
						value={term}
						onChange={(event) => onTermChange(event.target.value)}
						InputProps={{
							startAdornment: (
								<Mui.InputAdornment position="start">
									<Icons.Search size={18} />
								</Mui.InputAdornment>
							),
						}}
					/>
				</Mui.Grid>
				<Mui.Grid item>
					<FilterMenu
						classes={classes}
						sortOptions={sortOptions}
						sort={sort}
						onSortChange={onSortChange}
						filters={filters}
						onFiltersChange={onFiltersChange}
						officeOptions={officeOptions}
					/>
				</Mui.Grid>
				{showColumnControls && (
					<Mui.Grid item>
						<ColumnsMenu
							classes={classes}
							columnOptions={columnOptions}
							visibleColumns={visibleColumns}
							onColumnsChange={onColumnsChange}
						/>
					</Mui.Grid>
				)}
			</Mui.Grid>
			<Mui.Grid item container xs={12} md={4} spacing={1} justifyContent="flex-end">
				<Mui.Grid item>
					<Mui.Button
						className={`ihf-exportagents ${classes.actionButton}`}
						variant="outlined"
						color="primary"
						disabled={exporting}
						onClick={handleExport}
					>
						Export
					</Mui.Button>
				</Mui.Grid>
				<Mui.Grid item>
					<Mui.Button
						className={classes.actionButton}
						variant="contained"
						color="primary"
						startIcon={<Icons.Plus size={18} />}
						onClick={onAddMember}
					>
						Add Member
					</Mui.Button>
				</Mui.Grid>
			</Mui.Grid>
			<Mui.Snackbar
				open={exportFailed}
				autoHideDuration={4000}
				onClose={() => setExportFailed(false)}
			>
				<MuiAlert severity="error">
					<Mui.Typography>Failed to export team</Mui.Typography>
				</MuiAlert>
			</Mui.Snackbar>
		</Mui.Grid>
	);
};

type ToolbarClasses = ReturnType<typeof useStyles>["classes"];

interface FilterMenuProps {
	classes: ToolbarClasses;
	sortOptions: SortOption[];
	sort: Sort;
	onSortChange: (sort: Sort) => void;
	filters: TeamFilters;
	onFiltersChange: (filters: TeamFilters) => void;
	officeOptions: OfficeOption[];
}

const FilterMenu: React.FC<FilterMenuProps> = ({
	classes,
	sortOptions,
	sort,
	onSortChange,
	filters,
	onFiltersChange,
	officeOptions,
}) => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const activeCount = filters.roles.length + filters.routing.length + filters.offices.length + (filters.billable ? 1 : 0);

	const toggleRole = (role: RoleTag) => {
		const roles = filters.roles.includes(role)
			? filters.roles.filter((r) => r !== role)
			: [...filters.roles, role];
		onFiltersChange({ ...filters, roles });
	};

	const toggleRouting = (routing: RoutingTag) => {
		const next = filters.routing.includes(routing)
			? filters.routing.filter((r) => r !== routing)
			: [...filters.routing, routing];
		onFiltersChange({ ...filters, routing: next });
	};

	const toggleOffice = (officeId: number) => {
		const next = filters.offices.includes(officeId)
			? filters.offices.filter((id) => id !== officeId)
			: [...filters.offices, officeId];
		onFiltersChange({ ...filters, offices: next });
	};

	return (
		<>
			<Mui.Button
				className={classes.dropdownButton}
				endIcon={<Icons.ChevronDown size={16} />}
				onClick={(event) => setAnchorEl(event.currentTarget)}
			>
				{activeCount ? `Filter (${activeCount})` : "Filter"}
			</Mui.Button>
			<Mui.Menu
				anchorEl={anchorEl}
				open={!!anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				classes={{ paper: classes.menuPaper }}
				transitionDuration={0}
			>
				<Mui.Typography className={classes.sectionHeader}>Sort by</Mui.Typography>
				{sortOptions.map((option) => (
					<Mui.MenuItem
						key={option.id}
						className={classes.option}
						onClick={() => onSortChange({ id: option.id, desc: option.desc })}
					>
						<Mui.Radio
							className={classes.radio}
							checked={sort.id === option.id}
							disableRipple
						/>
						<Mui.ListItemText className={classes.optionLabel} primary={option.label} />
					</Mui.MenuItem>
				))}
				<Mui.Typography className={classes.sectionHeader}>Roles</Mui.Typography>
				{ROLE_OPTIONS.map((role) => (
					<Mui.MenuItem key={role} className={classes.option} onClick={() => toggleRole(role)}>
						<Mui.Checkbox
							className={classes.checkbox}
							checked={filters.roles.includes(role)}
							disableRipple
						/>
						<Mui.ListItemText className={classes.optionLabel} primary={ROLE_FILTER_LABELS[role]} />
					</Mui.MenuItem>
				))}
				{env.billingRefresh && <Mui.Typography className={classes.sectionHeader}>Billing</Mui.Typography>}
				{env.billingRefresh && (
					<Mui.MenuItem
						className={classes.option}
						onClick={() => onFiltersChange({ ...filters, billable: !filters.billable })}
					>
						<Mui.Checkbox
							className={classes.checkbox}
							checked={filters.billable}
							disableRipple
						/>
						<Mui.ListItemText className={classes.optionLabel} primary="Billable Lead Owners" />
					</Mui.MenuItem>
				)}
				<Mui.Typography className={classes.sectionHeader}>Routing</Mui.Typography>
				{ROUTING_OPTIONS.map((routing) => (
					<Mui.MenuItem key={routing} className={classes.option} onClick={() => toggleRouting(routing)}>
						<Mui.Checkbox
							className={classes.checkbox}
							checked={filters.routing.includes(routing)}
							disableRipple
						/>
						<Mui.ListItemText className={classes.optionLabel} primary={routing} />
					</Mui.MenuItem>
				))}
				{officeOptions.length > 0 && (
					<Mui.Typography className={classes.sectionHeader}>Offices</Mui.Typography>
				)}
				{officeOptions.map((office) => (
					<Mui.MenuItem key={office.id} className={classes.option} onClick={() => toggleOffice(office.id)}>
						<Mui.Checkbox
							className={classes.checkbox}
							checked={filters.offices.includes(office.id)}
							disableRipple
						/>
						<Mui.ListItemText className={classes.optionLabel} primary={office.label} />
					</Mui.MenuItem>
				))}
			</Mui.Menu>
		</>
	);
};

interface ColumnsMenuProps {
	classes: ToolbarClasses;
	columnOptions: AgentColumnType[];
	visibleColumns: AgentColumnType[];
	onColumnsChange: (columns: AgentColumnType[]) => void;
}

const ColumnsMenu: React.FC<ColumnsMenuProps> = ({
	classes,
	columnOptions,
	visibleColumns,
	onColumnsChange,
}) => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

	const toggleColumn = (column: AgentColumnType) => {
		const next = visibleColumns.includes(column)
			? visibleColumns.filter((c) => c !== column)
			: [...visibleColumns, column];
		onColumnsChange(next);
	};

	return (
		<>
			<Mui.Button
				className={classes.dropdownButton}
				endIcon={<Icons.ChevronDown size={16} />}
				onClick={(event) => setAnchorEl(event.currentTarget)}
			>
				Columns
			</Mui.Button>
			<Mui.Menu
				anchorEl={anchorEl}
				open={!!anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				classes={{ paper: classes.menuPaper }}
				transitionDuration={0}
			>
				{columnOptions.map((column) => (
					<Mui.MenuItem
						key={column.id}
						className={classes.option}
						onClick={() => toggleColumn(column)}
					>
						<Mui.Checkbox
							className={classes.checkbox}
							checked={visibleColumns.includes(column)}
							disableRipple
						/>
						<Mui.ListItemText className={classes.optionLabel} primary={COLUMN_TOGGLE_LABELS[column.id] ?? column.label} />
					</Mui.MenuItem>
				))}
			</Mui.Menu>
		</>
	);
};
